import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Loading skeleton for doctor rows
const DoctorRowSkeleton = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-silver/20"></div>
        <div>
          <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-32 mb-2"></div>
          <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
        </div>
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-40"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-6 bg-slate-200 dark:bg-silver/20 rounded w-16"></div>
    </td>
  </tr>
);

const AdminDoctors = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'admin')) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  const fetchDoctors = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'admin' || !token) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/admin/doctors`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (response.status === 403) {
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch doctors list');
      }

      const data = await response.json();
      setDoctors(data.doctors || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, token, navigate]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/admin/dashboard"
            className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth"
          >
            &larr; Back to Dashboard
          </Link>
        </div>
        <h1 className="font-display text-4xl italic tracking-tighter mb-2">
          Manage Doctors
        </h1>
        <p className="font-body text-light-text-secondary dark:text-silver/60">
          View and manage all registered doctors on the platform
        </p>
      </div>

      {/* API Error Alert */}
      {apiError && (
        <div className="mb-6">
          <ApiErrorAlert
            error={apiError}
            onRetry={fetchDoctors}
          />
        </div>
      )}

      {/* Stats Summary */}
      <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
        <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
          Total Doctors
        </p>
        <p className="font-display text-3xl italic">{doctors.length}</p>
      </div>

      {/* Doctors Table */}
      <div className="glass border border-light-border dark:border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border dark:border-border">
                <th className="px-6 py-4 text-left font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  Doctor
                </th>
                <th className="px-6 py-4 text-left font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  Email
                </th>
                <th className="px-6 py-4 text-left font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  Joined
                </th>
                <th className="px-6 py-4 text-left font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <DoctorRowSkeleton />
                  <DoctorRowSkeleton />
                  <DoctorRowSkeleton />
                </>
              ) : doctors.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <p className="font-body text-light-text-secondary dark:text-silver/60">
                      No doctors registered yet
                    </p>
                  </td>
                </tr>
              ) : (
                doctors.map((doctor) => (
                  <tr
                    key={doctor.id}
                    className="border-b border-light-border dark:border-border last:border-b-0 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {doctor.photo_url ? (
                          <img
                            src={doctor.photo_url}
                            alt={`Dr. ${doctor.first_name} ${doctor.last_name}`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-silver flex items-center justify-center">
                            <span className="font-mono text-sm text-obsidian">
                              {doctor.first_name?.[0]}{doctor.last_name?.[0]}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-body font-medium">
                            Dr. {doctor.first_name} {doctor.last_name}
                          </p>
                          {doctor.specialties && doctor.specialties.length > 0 && (
                            <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                              {Array.isArray(doctor.specialties)
                                ? doctor.specialties.slice(0, 2).join(', ')
                                : doctor.specialties}
                              {Array.isArray(doctor.specialties) && doctor.specialties.length > 2 && '...'}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-body text-light-text-secondary dark:text-silver/60">
                        {doctor.email}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                        {doctor.created_at
                          ? new Date(doctor.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono uppercase ${
                        doctor.stripe_connect_id
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {doctor.stripe_connect_id ? 'Active' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDoctors;
